import React, {
  memo,
  useCallback,
  useRef
} from 'react';

import useBooleanState from '@/hooks/useBooleanState';
import useElementEvent from '@/hooks/useElementEvent';
import { noop } from '@/utils';

import OrderForm, { OrderFormContent } from '@/components/Forms/OrderForm';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import Icon from '@/components/UI/Icon';

interface OrderFormModalProps {
  button: string;
  content: OrderFormContent;
  recaptchaSiteKey?: string;
  recaptchaDisabled?: boolean;
  onSuccess?: () => void;
}

const CLOSE_TIMEOUT = 10000;

const OrderFormModal = ({
  button,
  content,
  recaptchaSiteKey,
  recaptchaDisabled,
  onSuccess: onSuccessProps = noop
}: OrderFormModalProps) => {
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const {
    isOpen,
    open: openModal,
    close: closeModal
  } = useBooleanState();

  const onSuccess = useCallback(() => {
    timeout.current = setTimeout(closeModal, CLOSE_TIMEOUT);

    onSuccessProps();
  }, [
    closeModal,
    onSuccessProps
  ]);

  useElementEvent(`#${button}`, openModal);

  return (
    <Modal
      className="order-modal"
      isOpen={isOpen}
      onRequestClose={closeModal}
    >
      <div className="order-modal-content">
        <OrderForm
          content={content}
          recaptchaSiteKey={recaptchaSiteKey}
          recaptchaDisabled={recaptchaDisabled}
          onSuccess={onSuccess}
        />
      </div>

      <Button
        aria-label="Close"
        className="ReactModal__Close"
        onClick={closeModal}
      >
        <Icon
          color="#afafaf"
          name="Close"
          width={32}
          height={32}
        />
      </Button>
    </Modal>
  );
};

export default memo(OrderFormModal);
